import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, fmtDate, Loading } from "@/components/ui-bits";
import { Wrench, AlertTriangle, CheckCircle2, Clock, X, Loader2, ClipboardList, ChevronRight, FileText, Paperclip, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import TeamInbox from "@/components/TeamInbox";
import ContractorsPanel from "@/components/ContractorsPanel";
import MaintenanceThread from "@/components/MaintenanceThread";

const DAY = 86400000;

function RequestFiles({ requestId }) {
  const [files, setFiles] = useState([]);
  useEffect(() => {
    api.get(`/files?entity_type=maintenance&entity_id=${requestId}`).then(({ data }) => setFiles(data)).catch(() => {});
  }, [requestId]);
  if (files.length === 0) return null;
  return (
    <div className="mt-3" data-testid={`maint-files-${requestId}`}>
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><Paperclip size={12} /> Tenant photos &amp; files ({files.length})</p>
      <div className="flex flex-wrap gap-2">
        {files.map((f) => {
          const isImg = (f.content_type || "").startsWith("image/") || f.category === "photo";
          return isImg ? (
            <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" title={f.original_filename}
              data-testid={`maint-file-${f.file_id}`}
              className="block w-16 h-16 rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-blue-400 transition-shadow">
              <img src={fileUrl(f.file_id)} alt={f.original_filename} className="w-full h-full object-cover" />
            </a>
          ) : (
            <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" download
              data-testid={`maint-file-${f.file_id}`}
              className="text-xs bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 hover:bg-slate-200 transition-colors max-w-[170px]">
              <FileText size={13} className="text-red-500 shrink-0" /><span className="truncate">{f.original_filename}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function flagFor(r) {
  if (r.status === "Resolved") return { key: "done", label: "Completed", cls: "bg-emerald-50 border-emerald-200", badge: "text-emerald-700 bg-emerald-100", Icon: CheckCircle2, red: false };
  if (r.status === "Reported") return { key: "reported", label: "URGENT — needs categorising", cls: "bg-red-50 border-red-300", badge: "text-red-700 bg-red-100", Icon: AlertTriangle, red: true };
  // In Progress
  const since = r.categorised_at || r.date;
  const overdue = since && (Date.now() - new Date(since).getTime()) > 2 * DAY;
  if (overdue) return { key: "overdue", label: "OVERDUE — 2+ days, not completed", cls: "bg-red-50 border-red-300", badge: "text-red-700 bg-red-100", Icon: AlertTriangle, red: true };
  return { key: "inprogress", label: "In progress", cls: "bg-blue-50 border-blue-200", badge: "text-blue-700 bg-blue-100", Icon: Clock, red: false };
}

export default function Maintenance() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState(null); // request being categorised
  const [form, setForm] = useState({ assignee: "Maintenance", priority: "High", due_date: "" });
  const [saving, setSaving] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadBusy, setThreadBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/maintenance").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const complete = async (r) => {
    await api.patch(`/maintenance/${r.request_id}`, { status: "Resolved" });
    toast.success("Marked as job completed");
    load();
  };

  const sendReply = async (text) => {
    setThreadBusy(true);
    try {
      const { data } = await api.post(`/maintenance/${thread.request_id}/reply`, { text });
      setThread((prev) => ({ ...prev, messages: [...(prev.messages || []), data.message] }));
      toast.success(data.emailed ? "Reply sent & emailed to the tenant" : "Reply saved (no tenant email on file)");
      load();
    } catch { toast.error("Could not send reply"); }
    finally { setThreadBusy(false); }
  };

  const submitCat = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/maintenance/${cat.request_id}/categorise`, {
        assignee: form.assignee, priority: form.priority, due_date: form.due_date || undefined,
      });
      toast.success("Categorised — task created");
      setCat(null);
      load();
    } catch { toast.error("Could not categorise"); }
    finally { setSaving(false); }
  };

  if (!items) return <Layout title="Maintenance"><Loading /></Layout>;

  const statusRank = (r) => {
    const s = r.status;
    if (s === "Reported") return 0;              // new — needs action first
    if (s === "Resolved") return 2;              // completed — last
    return 1;                                    // in progress / everything else
  };
  const sorted = [...items].sort((a, b) => {
    const ra = statusRank(a), rb = statusRank(b);
    if (ra !== rb) return ra - rb;
    return new Date(b.date) - new Date(a.date);
  });
  const urgent = sorted.filter((r) => flagFor(r).red).length;

  return (
    <Layout title="Maintenance" subtitle="Maintenance Centre">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">{items.length} request{items.length === 1 ? "" : "s"}</span>
          {urgent > 0 && <span data-testid="maintenance-urgent-count" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle size={12} /> {urgent} need attention</span>}
        </div>
        <button data-testid="open-work-orders-button" onClick={() => navigate("/work-orders")}
          className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
          <ClipboardList size={16} /> Work Orders <ChevronRight size={15} />
        </button>
      </div>

      <TeamInbox department="Maintenance" />

      <ContractorsPanel />

      <div className="space-y-4">
        {sorted.length === 0 && <Card className="p-10 text-center text-slate-400 text-sm">No maintenance requests.</Card>}
        {sorted.map((r) => {
          const f = flagFor(r);
          return (
            <Card key={r.request_id} data-testid={`maintenance-${r.request_id}`} className={`p-5 border ${f.cls} fade-up`}>
              <div className="flex items-start gap-3">
                <f.Icon size={22} className={f.red ? "text-red-600 mt-0.5" : "text-slate-500 mt-0.5"} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${f.badge}`} data-testid={`maintenance-flag-${r.request_id}`}>{f.label}</span>
                    <Badge>{r.priority}</Badge>
                    <span className="text-xs text-blue-700">{r.property_address || r.property_ref}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900">{r.title}</h3>
                  {r.description && <p className="text-sm text-slate-600 mt-0.5">{r.description}</p>}
                  <p className="text-xs text-slate-400 mt-1.5">{r.tenant_name ? `${r.tenant_name} · ` : ""}Reported {fmtDate(r.date)}{r.categorised_at ? ` · Categorised ${fmtDate(r.categorised_at)}` : ""}</p>
                  <RequestFiles requestId={r.request_id} />
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {r.status === "Reported" && (
                    <button data-testid={`new-work-order-${r.request_id}`} onClick={async () => {
                        try { await api.patch(`/maintenance/${r.request_id}`, { status: "In Progress" }); } catch {}
                        navigate("/work-orders", { state: { prefill: { title: r.title, property_ref: r.property_ref, property_id: r.property_id, request_id: r.request_id, description: r.description || "", priority: r.priority || "High" } } });
                      }}
                      className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5">
                      <ClipboardList size={14} /> New work order
                    </button>
                  )}
                  {r.status === "In Progress" && (
                    <span data-testid={`in-progress-${r.request_id}`} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-100 text-amber-700 whitespace-nowrap flex items-center gap-1.5">
                      <Clock size={13} /> In progress
                    </span>
                  )}
                  {r.status === "In Progress" && (
                    <button data-testid={`complete-${r.request_id}`} onClick={() => complete(r)}
                      className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap">
                      Mark job completed
                    </button>
                  )}
                  <button data-testid={`reply-tenant-${r.request_id}`} onClick={() => setThread(r)}
                    className="relative border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5">
                    <MessageSquare size={14} /> Reply to tenant
                    {r.messages && r.messages.length > 0 && r.messages[r.messages.length - 1].sender === "tenant" && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" data-testid={`reply-badge-${r.request_id}`} />
                    )}
                  </button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {thread && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setThread(null)}>
          <div data-testid="maint-thread-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold text-slate-900">Reply to tenant</h3>
              <button onClick={() => setThread(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">{thread.title} · {thread.tenant_name || "Tenant"} · {thread.property_address || thread.property_ref}</p>
            <MaintenanceThread messages={thread.messages || []} side="staff" busy={threadBusy} onSend={sendReply}
              placeholder="Message the tenant about access times…"
              quickReplies={["Contractor can attend Tue/Wed AM.", "Please confirm you'll be home.", "Please suggest 2–3 times that suit you.", "We'll need access between 9am and 1pm."]} />
          </div>
        </div>
      )}

      {cat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setCat(null)}>
          <div data-testid="categorise-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold text-slate-900">Categorise into task</h3>
              <button onClick={() => setCat(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-5">{cat.title} · {cat.property_address || cat.property_ref}</p>
            <form onSubmit={submitCat} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Assign to</label>
                <input data-testid="cat-assignee" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Priority</label>
                  <select data-testid="cat-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {["Urgent", "High", "Medium", "Low"].map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Due date</label>
                  <input data-testid="cat-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
              </div>
              <button data-testid="submit-categorise" type="submit" disabled={saving}
                className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={16} className="animate-spin" /> : null} Create Task & Mark In Progress
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
